import React, { useState } from 'react'
import injectSheet from 'react-jss'
import Sheet from '../../sheet/sheet'
import Section from '../../shared/section/section'
import CircleStatus from 'pobble-components/dist/circle-status'
import PobbleLink from 'pobble-components/dist/pobble-link'
import Segment from '../../shared/segment/segment'
import Alert from 'pobble-components/dist/alert'
import Tiers from './tiers/tiers'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import Button from 'pobble-components/dist/button'
import { goToSelfServicePortal } from 'pobble-components/dist/subscriptions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoneyCheckEdit } from '@fortawesome/pro-regular-svg-icons/faMoneyCheckEdit'
import posthog from 'posthog-js'
import { useMe } from 'pobble-components/dist/pobble-user'

const ManageSubscriptionButton = injectSheet(({ COLORS }) => ({
  reloadMessage: {
    margin: '0',
    color: COLORS.RED.NORMAL,
    fontWeight: 'bold'
  }
}))(({ classes }) => {
  const [pendingPortal, setPendingPortal] = useState(false)
  return (
    <Button
      tall
      secondary
      icon={<FontAwesomeIcon icon={faMoneyCheckEdit} />}
      disabled={pendingPortal}
      action={() => {
        posthog.capture('click on manage subscription')
        setPendingPortal(true)
        goToSelfServicePortal()
      }}>
      {!pendingPortal && 'Manage my current subscription'}
      {pendingPortal && 'Loading...'}
    </Button>
  )
})

const styles = {
  helpSection: {
    paddingTop: '22px',
    paddingBottom: '0',
    borderBottom: 'none'
  },
  membership: {
    paddingTop: '10px',
    paddingBottom: '30px',
    fontSize: '16px'
  },
  schoolStatusHolder: {
    marginTop: '32px',
    display: 'flex',
    '& $status': {
      color: 'var(--blue)'
    },
    '& p': {
      margin: '0'
    }
  },
  statusHolder: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      margin: '0'
    }
  },
  status: {
    margin: '0 10px 0 0',
    flexShrink: '0'
  },
  moderationStatusHolder: {
    marginTop: '16px'
  },
  moderationStatus: {
    margin: '0 10px 0 0',
    flexShrink: '0',
    color: 'var(--purple)'
  },
  error: {
    color: 'var(--red)'
  },
  errorSection: {
    display: 'block',
    paddingTop: '20px',
    '& a': {
      color: 'currentColor',
      fontWeight: 'bold'
    }
  },
  buttonSection: {
    paddingLeft: '50px',
    paddingTop: '0',
    alignItems: 'flex-start'
  },
  tiersSegment: {
    alignItems: 'center',
    paddingBottom: '30px'
  },
  billingError: {
    display: 'inline-flex'
  },
  cancelSubscription: {
    marginTop: '20px'
  },
  cancelSection: {
    paddingTop: '22px'
  },
  statusLabel: {
    font: 'var(--body--)',
    display: 'block'
  },
  upgradeAnnual: {
    color: 'var(--red)',
    marginLeft: '8px',
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none'
    }
  },
  schoolUpsellSection: {
    marginTop: '16px',
    fontSize: '14px',
    '& a': {
      marginLeft: '6px'
    }
  }
}

const Membership = ({ classes }) => {
  const {
    isInPobbleSchool,
    isInModerationSchool,
    individualPlan,
    cancelled,
    dueInvoice,
    schoolPlanTitle
  } = useSubscriptionDetails()
  const { me } = useMe()
  const isParent = me.account.role_in_life === 'parent'

  return (
    <Sheet>
      <Section title="Your subscription" className={classes.membership}>
        <Segment size="M">
          <div className={classes.statusHolder}>
            {individualPlan && (
              <p>
                <ManageSubscriptionButton />
              </p>
            )}
            {!individualPlan &&
              !isParent && (
                <>
                  <CircleStatus status={true} className={classes.status} />

                  <p>
                    <label className={classes.statusLabel}>Individual:</label>
                    <strong>Free</strong>
                  </p>
                </>
              )}
            {!individualPlan &&
              isParent && (
                <>
                  <CircleStatus status={'pending'} className={classes.status} />

                  <p>
                    <label className={classes.statusLabel}>Pobble Home:</label>
                    <strong>No active subscription yet</strong>
                  </p>
                </>
              )}
          </div>
          {individualPlan &&
            !isParent && (
              <div className={classes.schoolUpsellSection}>
                <span role="img" aria-label="lightbulb">
                  💡
                </span>
                Don’t want to pay for Pobble yourself? Your school could
                subscribe instead.
                <PobbleLink
                  href="https://www.pobble.com/schools"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    posthog.capture(
                      'click on school referral form link from membership page'
                    )
                  }>
                  See how it works.
                </PobbleLink>
              </div>
            )}
          {(isInPobbleSchool || isInModerationSchool) && (
            <div className={classes.schoolStatusHolder}>
              <CircleStatus status={true} className={classes.status} />
              <p>
                <label className={classes.statusLabel}>
                  School subscription:
                </label>
                <strong>{schoolPlanTitle}</strong>
              </p>
            </div>
          )}
        </Segment>
        {dueInvoice && (
          <Segment size="L" className={classes.errorSection}>
            <Alert
              className={classes.billingError}
              statusText="We have not received or been able to complete your payment."
              type="warning">
              <br />
              To review your payment methods please click the ‘manage
              subscription’ button.
            </Alert>
          </Segment>
        )}
      </Section>
      <Section>
        <Segment className={classes.tiersSegment} size="L">
          <Tiers active={!cancelled && individualPlan} />
        </Segment>
      </Section>
      <Section className={classes.helpSection}>
        <Segment size="L">
          <div>
            Your membership page/checkout currency may differ based on your
            location and the location of your school.
            <br />
            For any questions, please email{' '}
            <PobbleLink href="mailto:hello@pobble.com">
              hello@pobble.com
            </PobbleLink>
          </div>
        </Segment>
      </Section>
    </Sheet>
  )
}

export default injectSheet(styles)(Membership)
